/**
Player Audio
============

Main audio loop

Needs tempo, playable, start beat, end beat, looping mode to play.

Provides currentBeat and playing methods.
*/

{ Modal, Observable, Progress } from @danielx/dui

{ceil, cos, floor, max, min, PI} := Math

// Safari uses webkit prefix.
OfflineAudioContext := window.OfflineAudioContext or window.webkitOfflineAudioContext

{ MidiChannel, ChannelData, MidiEvent, REVERB_DRAIN_SECONDS, SF2Synth, SpessaSynth, clamp, soundBankFormat } from @danielx/lib
type { SynthEngine } from @danielx/lib
MidiSong from ./lib/midi/song.civet

liveContext from ./lib/audio-context.civet
{ audioBufferToMP3, audioBufferToWav, audioBufferToChannelData } from ./lib/encoder/index.civet
SHALoader from ./lib/sha-loader.civet

{
  DEFAULT_SYNTH_SHA
  GENERAL_USER_GS_SHA
  LEGACY_SYNTH_SHA
  US_PER_S: microsecondsPerSecond
} from ./const.civet

{
  quantize
  eventToElementPosition
} from ./lib/util.civet
FX from ./lib/fx.civet
{limiter, stereoAnalyser} := FX
{ buildEffectGraph } from ./lib/effect-graph.civet

FXNetwork := (destination: AudioNode) ->
  {context} := destination
  gain := context.createGain()
  gain.connect destination

  fx := FX.choices.reduce (o, name) ->
    o[name] = FX[name] gain
    return o
  , ({} as Record<typeof FX["choices"][number], GainNode>)

  default: gain
  fx: fx
  dispose: ->
    {currentTime} := context
    gain.gain.linearRampToValueAtTime 0, 0.1 + currentTime

    setTimeout ->
      gain.disconnect()
    , 0.25

/**
PlayerAudio is a mixin that provides audio playback functionality.
It is responsible for setting up the audio context, passing MIDI
events to the synth, and connecting the synth to the audio context.
*/
export PlayerAudio := <Base extends Constructor<{}>>(S: Base) ->
  abstract class PlayerAudio extends S
    abstract activeChannel: () => number
    abstract afxPresetName: () => typeof FX["choices"][number]
    abstract length: () => number
    abstract showLoading(message: string): () => void
    abstract song: MidiSong
    abstract trigger(event: string, ...parameters: any[]): this
    abstract on(event: string, callback: Function): this

    analysedDestination: ReturnType<typeof stereoAnalyser>

    dispatchImmediateMidiData: (data: Uint8Array) => void
    songTimeAtPerformanceTime: (perfMs: number) => number
    // Provided by the PracticeMode mixin: scheduled events it returns true for
    // are withheld from the synth (the part being practiced). Immediate events
    // — the player's own input — never pass through this.
    suppressScheduledEvent?: (e: MidiEvent) => boolean
    // Provided by the PracticeMode mixin: playback rate multiplier (0.5–1)
    // applied at the clock, not the tempo map, and a [startBeat, endBeat]
    // practice loop the transport wraps within.
    playbackRate?: () => number
    practiceLoopRange?: () => [number, number]?
    // Wait-for-note: the song time (seconds) the clock must freeze at until
    // the player supplies the gated notes, or undefined when nothing blocks
    practiceGateTime?: () => number?
    // Game Mode: a pass is one play-through, so the song's whole-song loop is
    // ignored and the transport stops at the end (which shows the results)
    suppressSongLoop?: () => boolean

    debugSynth: () => void
    downloadSF2: () => void

    playing = Observable false
    playBuffer: (buffer: AudioBuffer, rate?: number, time?: number, volume?: number, dest?: {context: BaseAudioContext}) => AudioBufferSourceNode
    playNote: (c?: number, n?: number, v?: number, dt?: number, dest?: {context: BaseAudioContext}) => void
    releaseNote: (c?: number, n?: number, v?: number, dt?: number, dest?: {context: BaseAudioContext}) => void
    virtualKeyboard: () => void
    playFromStart: () => void
    pause: () => void
    stop: () => void

    setSf2Synth: (buffer: ArrayBuffer, options?: {attenuationDbPerUnit?: number}) => void
    synth: () => SynthEngine
    // Settles once the latest reset's soundfont is in and playback is set up
    audioReady: Promise<unknown> = Promise.resolve()

    initPlayerAudio: () => void

    constructor(...args: any[])
      super(...args)

      // The destination for 'live audio' goes to the analyser node then the speakers.
      // We add a hard limiter on right before the analyser and output.
      analysedDestination := stereoAnalyser liveContext.destination
      @analysedDestination = analysedDestination
      liveDestination := limiter analysedDestination

      // FX Networks are disposable so we can fade out when stopping / restarting
      activeDestination := FXNetwork(liveDestination)

      // The engine: SpessaSynth, spessasynth_core in one AudioWorklet, is the default since
      // 2026-09-19 (docs/synth-engine-spike.md, docs/spessa-calibration.md). `?synth=sf2`
      // or `localStorage.synthEngine = "sf2"` is the escape hatch back to the per-voice
      // node graph until it is retired. The worklet processor is a separate served file
      // (build/build.civet copies it from lib), fetched when the engine is created.
      synthEngine := new URLSearchParams(location.search).get("synth") ?? localStorage.getItem("synthEngine")
      SpessaSynth.processorUrl = "lib/spessa-processor.js"
      synth: SynthEngine := if synthEngine is "sf2" then new SF2Synth() else new SpessaSynth()

      // TODO: Init this properly, need a reference here so we can play notes in
      // response to UI actions
      let context: AudioContext,
        contextReady: true?

      channelData := new ChannelData 24, synth

      // The song's effect graph (lib/effect-graph.civet), rebuilt on every initPlay at the
      // new position; undefined for songs without one.
      let activeGraph: ReturnType<typeof buildEffectGraph>?

      initialChannelState := channelData.stateSnapshot()

      let eventIterator: ReturnType<typeof MidiSong.prototype.eventIterator>?
      lookahead .= 0.25 // seconds
      playStartTime .= 0 // seconds
      contextStartTime .= playStartTime // seconds
      // Rate in effect since the last anchor (initPlay or a mid-play rate
      // change): song seconds advance at `playRate` per context second.
      playRate .= 1

      let midiEventHandler: (e: MidiEvent) => void

      // All scheduled playback funnels through here so practice mode can mute
      // the practiced part while the backing channels play on.
      scheduledEventHandler := (e: MidiEvent) =>
        return if @suppressScheduledEvent and @suppressScheduledEvent(e)
        midiEventHandler e

      // Need more lookahead to play when window loses focus. requestAnimationFrame
      // stops and intervals get sparser
      document.addEventListener "visibilitychange", (e) ->
        if document.hidden
          lookahead = 1.25
        else
          lookahead = 0.25
        bufferAudio()

      initPlay := =>
        {context} := activeDestination.default
        synth.prepareContext(context)
        .catch (e) ->
          // A worklet that fails to register (blocked module, unsupported context) must
          // not leave the scheduler waiting forever: the reverb bus already falls back
          // to dry inside `bind`, so play on without the worklets.
          console.warn "SF2 worklets unavailable, playing without them", e
        .then -> // worklets loaded (or given up on)
          contextReady = true

        //@ts-ignore
        context.resume()

        song := @song

        midiEventHandler = channelData.handleEvent

        contextStartTime = context.currentTime
        playStartTime = song.timeAtBeat(@currentBeat())
        playRate = @playbackRate?() ?? 1

        channelData.restoreSnapshot initialChannelState
        channelData.ticksPerBeat = song.ticksPerBeat
        channelData.rate = playRate
        // channelData accumulates song seconds scaled by 1/rate, so the replay
        // from 0 to playStartTime adds playStartTime/rate and lands on
        // contextStartTime.
        channelData.sync contextStartTime - playStartTime / playRate

        activeGraph?.dispose()
        activeGraph = undefined
        synthOut := synth.bind(channelData.channels, context)
        fxDest := activeDestination.fx[@afxPresetName()]
        if spec := song.effects?()
          activeGraph = buildEffectGraph context, spec, song, synthOut, (synth as any).taps?(context),
            contextStartTime: contextStartTime
            playStartTime: playStartTime
            rate: playRate
          activeGraph.output.connect fxDest
          activeGraph.ready.catch (e) -> console.warn "effect graph: an asset failed to load", e
        else
          synthOut.connect fxDest

        // sets all channel data (pitchBend, fx, etc) in addition to tempo
        // TODO: Optimize by using a snapshot rather than replaying all events
        synth.preparing = true
        eventIterator = song.eventIterator()
        try
          eventIterator.consumeUntilNoteOn(scheduledEventHandler)
          eventIterator.consumeUntilTime(playStartTime, scheduledEventHandler)
        finally
          // `preparing` stuck on would mute every note of the song
          synth.preparing = false

        @trigger 'initPlay'

        return

      bufferAudio := =>
        if contextReady and @playing()
          length := @length()
          return if length < 1 or isNaN(length)

          {context} := liveDestination
          {currentTime} := context

          song := @song

          rate := @playbackRate?() ?? 1
          if rate is not playRate
            // Re-anchor so time elapsed before the change keeps its old rate.
            // channelData needs no resync — it accumulates incrementally, so
            // the new rate only shapes deltas from here on. (Events already
            // scheduled into the lookahead window keep their old-rate times —
            // a sub-quarter-second transient.)
            playStartTime += (currentTime - contextStartTime) * playRate
            contextStartTime = currentTime
            playRate = rate
            channelData.rate = rate

          // t is "now" seconds into the song
          t := (currentTime - contextStartTime) * rate
          targetTime .= playStartTime + t + lookahead * rate

          // A/B practice loop: schedule nothing past B, and wrap back to A
          // through initPlay's replay so CC/program state is correct.
          loopRange := @practiceLoopRange?()
          if loopRange
            targetTime = min targetTime, song.timeAtBeat(loopRange[1])

          // Wait-for-note: schedule nothing past the gate either
          gateTime := @practiceGateTime?()
          if gateTime?
            targetTime = min targetTime, gateTime

          eventIterator!.consumeUntilTime(targetTime, scheduledEventHandler)

          beat .= song.beatAtTime(playStartTime + t)

          // Checked before the gate so a gate sitting exactly on B never
          // blocks the wrap — the loop is [A, B).
          if loopRange and beat >= loopRange[1]
            synth.allSoundsOff() // quick-fade the ringing tail
            @currentBeat loopRange[0]
            initPlay()
            return

          if gateTime? and playStartTime + t >= gateTime
            // Freeze: hold the anchor rebase at the gate instead of suspending
            // the context, so backing notes keep ringing. Judgment maps input
            // through this same anchor, so a note played here lands on time.
            playStartTime = gateTime
            contextStartTime = currentTime
            @currentBeat song.beatAtTime(gateTime)
            return

          if beat >= length
            if song.loop() and not @suppressSongLoop?.()
              beat -= length

              contextStartTime = context.currentTime
              playStartTime = song.timeAtBeat(beat)

              channelData.restoreSnapshot initialChannelState
              channelData.ticksPerBeat = song.ticksPerBeat
              channelData.rate = rate
              channelData.sync contextStartTime - playStartTime / rate

              targetTime = playStartTime + lookahead * rate
              eventIterator = song.eventIterator()
              eventIterator.consumeUntilTime(targetTime, scheduledEventHandler)

            else
              beat = length
              @playing false

          // Set currentBeat to match the audio context
          @currentBeat beat

        return

      setInterval ->
        bufferAudio()
      , 1000 / 120

      animBuffer := ->
        requestAnimationFrame animBuffer
        bufferAudio()
      animBuffer()

      // Plays a buffer directly into the live audio context destination or the
      // given destination. Rate and time are given to shift pitch and start time.
      // time is in seconds relative to the currentTime of the destination's
      // context.
      //
      // This is used for the 'eraser' sound effect.
      // time is in seconds, must be >= 0
      @playBuffer = (buffer, rate=1, time=0, volume=0.787, dest=liveDestination) =>
        {context} := dest

        // sometimes time comes in very slightly negative o_o, maybe a rounding error?
        // Audio context throws an error if start is called with a negative argument
        unless time >= 0
          time = 0

        source := context.createBufferSource()
        source.buffer = buffer
        source.start(time + context.currentTime)
        source.playbackRate.value = rate

        gain := context.createGain()
        gain.gain.value = volume

        source.connect gain
        //@ts-expect-error
        gain.connect(dest or context.destination)

        return source

      @playNote = (c=0, n=60, v=0x60, dt=0, dest?: {context: BaseAudioContext}) =>
        { context } := dest or activeDestination.fx[@afxPresetName()] or liveDestination
        //@ts-expect-error
        context.resume()
        t := context.currentTime

        channel := channelData.channels[c]

        synth.noteOn(channel, n, v, t+dt)

      @releaseNote = (c=0, n=60, v=0x40, dt=0, dest?: {context: BaseAudioContext}) =>
        { context } := dest or activeDestination.fx[@afxPresetName()] or liveDestination
        //@ts-expect-error
        context.resume()
        t := context.currentTime

        channel := channelData.channels[c]

        synth.noteOff(channel, n, v, t+dt)

      @playFromStart = =>
        if @playing()
          @stop()
        else
          @currentBeat 0
          @playing true
          initPlay()

      @pause = =>
        if @playing()
          @stop()
        else
          @playing true
          initPlay()

      @stop = =>
        @playing false
        // Quick fade
        synth.allSoundsOff()
        @trigger "stop"
        return

      @synth = =>
        @synthUpdated()

        synth

      @setSf2Synth = (buffer, options?) =>
        await synth.load buffer, options

        sha := synth.sha!
        name := synth.name!

        // Update song meta data so we can link back to synth
        @song.synthSha sha
        @song.synth name

        @registerSoundFont
          name: name
          sha: sha

        @synthUpdated.increment()

      @virtualKeyboard = =>
        // NOTE: This is all prototype code
        vk := document.createElement 'div'
        //@ts-ignore
        vk.style = "width: 100%; height: 128px; background-color: #080; display: flex; align-items: center; justify-content: center"
        vk.innerText = "virtual keyboard"

        document.body.appendChild vk

        let held: { channel: MidiChannel, note: number }?

        vk.addEventListener 'mousedown', (e) =>
          {x, y} :=  eventToElementPosition e, vk
          channel := channelData.channels[@activeChannel()]
          note := clamp 0, 127, floor (128 * x / vk.clientWidth)
          held =
            channel: channel
            note: note
          synth.noteOn(channel, note, 0x40, context.currentTime)
        vk.addEventListener 'mouseup', (e) =>
          if held
            {channel, note} := held
            synth.noteOff(channel, note, 0x40, context.currentTime)
            held = undefined

      @initPlayerAudio = =>
        @on "reset", =>
          @stop()
          @currentBeat 0

          @audioReady = Promise.resolve()
          .then =>
            synthName := @song.synth()
            synthSha := @song.synthSha()

            if synthSha?
              if synth.sha !== synthSha
                @setSynthBySha(synthSha)
            else
              // Songs that never named a synth fall through to the General MIDI bank.
              // Was "4MB GMGSMT" (Ct4mgm.sf2), which the app had to pull at runtime from
              // a third-party Windows driver pack on GitHub — an unowned URL carrying a
              // Creative Labs bank with no redistribution grant. GeneralUser GS ships
              // with the app instead, so this path no longer needs the network.
              @setSynth synthName or "GeneralUser GS"

          .then ->
            initPlay()

      SHALoader.readSoundFontChoicesCache()
      .then (choices) =>
        // TODO: merge in default choices?
        @soundFontChoices choices
      .catch =>
        // The three that ship with the app, so this list resolves with no network.
        @soundFontChoices [{
          name: "GeneralUser GS"
          sha: GENERAL_USER_GS_SHA
        }, {
          name: "Paint Composer Classic"
          sha: DEFAULT_SYNTH_SHA
        }, {
          name: "Paint Composer Legacy"
          sha: LEGACY_SYNTH_SHA
        }]

      @dispatchImmediateMidiData = (data: Uint8Array): void =>
        event := new MidiEvent channelData.currentTick, data
        midiEventHandler event

      // Map a performance.now()-domain timestamp (a Web MIDI event's
      // timeStamp) to seconds into the song. getOutputTimestamp pairs the
      // audio output clock with the performance clock, so the result is the
      // song time the player was HEARING at that instant — output latency
      // falls out of the comparison. Only meaningful while playing.
      @songTimeAtPerformanceTime = (perfMs: number) =>
        {context} := liveDestination
        //@ts-ignore getOutputTimestamp is on AudioContext, not BaseAudioContext
        ts := context.getOutputTimestamp?.()
        contextTime := if ts?.contextTime?
          ts.contextTime + (perfMs - ts.performanceTime!) / 1000
        else
          context.currentTime + (perfMs - performance.now()) / 1000

        playStartTime + (contextTime - contextStartTime) * playRate

      @debugSynth = =>
        console.log synth.debugStatus()

      @downloadSF2 = =>
        // The bytes as loaded, so the extension follows the container (SF2, SF3, DLS).
        synth.toBlob().download `synth.${soundBankFormat(synth.buffer!) ?? "sf2"}`

    soundFontChoices = Observable [] as {name: string, sha: string}[]
    registerSoundFont = (entry: {name: string, sha: string}) =>
      // TODO: check for existing and prompt for replacement
      existingChoices := @soundFontChoices()

      added .= false

      choices := existingChoices.map (choice) ->
        if entry.name is choice.name
          added = true
          entry
        else
          choice

      unless added
        choices.push entry

      choices.sort (a, b) ->
        a.name.localeCompare(b.name)

      SHALoader.cacheSoundFontChoices choices

      @soundFontChoices choices

    // Load and set the given synth
    setSynth = (name: string) =>
      // Display loading soundfont message
      loaded .= false
      hideLoading: (() => void)? .= undefined
      setTimeout =>
        unless loaded
          hideLoading = @showLoading "Loading Soundfont..."
      , 500

      try
        @setSf2Synth await SHALoader.byName name
      finally
        loaded = true
        hideLoading?()

    setSynthBySha = (sha: string) =>
      return unless sha?

      if @synth().sha is sha
        return

      // Display loading soundfont message
      loaded .= false
      hideLoading: (() => void)? .= undefined
      setTimeout =>
        unless loaded
          hideLoading = @showLoading "Loading Soundfont..."
      , 500

      SHALoader.bySha(sha)
      .then @setSf2Synth
      .finally ->
        loaded = true
        hideLoading?()

    playPatternPreview = (pattern: Pattern) =>
      tempo := 500000 // us/beat
      ticksPerBeat := pattern.ticksPerBeat
      rate := 8 // preview is 8x faster

      // Play pattern note sounds
      pattern.events.forEach (e) =>
        {channel, note, tick, type, v} := e

        dt := (tick / ticksPerBeat) * tempo / microsecondsPerSecond / rate

        if type is "noteOn"
          @playNote channel, note, v, dt
        else if type is "noteOff"
          @releaseNote channel, note, v, dt

    // Current play time in beats from beginning of song
    currentBeat = Observable 0
    currentTime = =>
      @song.timeAtBeat(@currentBeat())

    // Adjust `currentBeat` by `deltaBeats`, quantized to `q`
    adjustPlayhead = (deltaBeats: number, q=0) =>
      beat := quantize @currentBeat() + deltaBeats, q

      @setPlayhead(beat)

    setPlayhead = (beat: number) =>
      @currentBeat clamp(beat, 0, @song.beatLength())

    play = =>
      @pause()

    rewind = =>
      @stop()
      @currentBeat 0

    renderOfflineContext = renderOfflineContext
    synthUpdated = Observable 0

    exportSong = (song: MidiSong, opts: ExportSongOptions = {}) =>
      @stop()

      {name, sampleRate, type} .= opts

      name ?= "song"
      type ?= "mp3"
      sampleRate ?= 48000
      audioChannels := 2

      extension := `.${type}`

      progressView := Progress
        value: 0
        max: 1
        message: "Preparing Audio..."

      Modal.show progressView.element,
        cancellable: false

      cleanup := ->
        Modal.hide()

      try
        audioBuffer := await renderOfflineContext({
          song, @synth(), progressView, audioChannels, sampleRate
        })

        blob := await if type is "mp3"
          progressView.message "Encoding mp3..."
          audioBufferToMP3(audioBuffer)
        else
          progressView.message "Encoding wav..."
          audioBufferToWav(audioBuffer)

        blob.download name + extension

      finally
        cleanup()

  return PlayerAudio

export default PlayerAudio

export type PlayerAudioInstance = InstanceType<ReturnType<typeof PlayerAudio>>

type RenderOfflineContextOptions = {
  song: MidiSong
  synth: SynthEngine
  progressView: ReturnType<typeof Progress>
  audioChannels: number
  sampleRate: number
  fps?: number
  frameCallback?: (t: number, analyser: ReturnType<typeof stereoAnalyser>) => void
  startTime?: number
  duration?: number
  // Exact number of frames to emit, spanning `duration` seconds of MUSIC. Without it
  // frames land on a 1/fps grid and the count falls out of the arithmetic, which is
  // half a frame of A/V length mismatch whenever the clip isn't frame-aligned (a song
  // is a musical length, not a multiple of 1/30s). With it the frame step becomes
  // duration/frameCount and the caller resamples the audio to match — see renderVideo.
  frameCount?: number
  // Seconds of decay to render past the end and fold back onto the beginning, for a clip
  // that will be played on repeat. Without it the clip ends at whatever level the music
  // happened to be at — Mushroom Forest is at -17dBFS on the last sample and starts from
  // digital silence, and that one-sample step IS the click. Folding the tail in makes the
  // seam sound like the app's own playback loop: the notes ringing at the end ring on over
  // the restart.
  loopTail?: number
  // Linear master gain applied ahead of the limiter. The limiter sits at threshold 0dB
  // with a 5ms attack, so it does not save a hot signal — transients clip flat. Loud
  // soundfonts (GeneralUser GS runs ~12dB hotter than the app default) need headroom
  // made here rather than taken away afterwards.
  gain?: number
  // dBFS the render's limiter engages at. Below 0 so its attack has somewhere to catch
  // overshoot instead of letting it clip.
  limiterThreshold?: number
}

// The render's limiter is a DynamicsCompressorNode, and the browser's compressor looks
// ahead: Chromium delays its output by 6ms (288 samples at 48k) at every setting, even when
// it is not compressing. Every render's audio landed that far behind the frames and the
// MIDI timeline, so the slice in renderOfflineContext reads the buffer this much later.
// Measured on an impulse rather than assumed, once per sample rate.
limiterLatencies := new Map<number, Promise<number>>
limiterLatency := (sampleRate: number) ->
  cached := limiterLatencies.get sampleRate
  return cached if cached

  context := new OfflineAudioContext 1, 2048, sampleRate
  impulse := context.createBuffer 1, 2048, sampleRate
  impulse.getChannelData(0)[0] = 0.1
  source := context.createBufferSource()
  source.buffer = impulse
  source.connect limiter context.destination
  source.start 0

  latency := context.startRendering().then (buffer) ->
    out := buffer.getChannelData 0
    peak .= 0
    for i of [1...out.length]
      peak = i if Math.abs(out[i]) > Math.abs(out[peak])
    peak / sampleRate
  limiterLatencies.set sampleRate, latency
  return latency

renderOfflineContext := ({
  song
  synth
  progressView
  audioChannels
  sampleRate
  fps
  frameCallback
  startTime = 0
  duration
  frameCount
  loopTail = 0
  gain = 1
  limiterThreshold = -6
}: RenderOfflineContextOptions) ->

  lengthInSeconds := song.secondsLength()

  fps ||= 30
  duration ?= lengthInSeconds
  endTime := min startTime + duration, lengthInSeconds
  duration = endTime - startTime

  // Callback to sync vfx frame rendering
  resolved := Promise.resolve()
  // noop
  frameCallback ?= -> resolved

  // Frame step. `frameCount` asks for an exact number of frames across `duration`, so
  // the step is duration/frameCount rather than 1/fps — they differ by well under a
  // millisecond, and that difference is what makes the frame count exact.
  dt := if frameCount then duration/frameCount else 1/fps // seconds
  // Frames past this index are still stepped through (the events after the last frame
  // are part of the mix) but not handed to the caller.
  frameLimit := frameCount ?? Infinity

  // Always render from t=0, because a note held from before `startTime` has to already
  // be sounding when the window opens and there's no way to trigger that accurately from
  // a cold start. But stop just past `endTime` — everything after it is discarded by
  // audioBufferToChannelData below, so rendering the rest of the song is pure waste.
  // (A 40s clip of a 187s song was paying for all 187s of audio.)
  //
  // One frame of pad, so the loop's final suspend at exactly t == endTime still lands
  // strictly inside the buffer rather than at its very edge. The pad is discarded with
  // the rest of the tail — except for `loopTail` seconds of it, which get folded back
  // onto the head below.
  //
  // The reverb bus, when the synth has one, needs longer than the loop fold does: its tail
  // is still ringing `REVERB_DRAIN_SECONDS` after the last note, and a buffer that ends
  // before that cuts the decay off at whatever level it happened to be at. Those extra
  // seconds are rendered and then thrown away by the slice below — they exist only to give
  // the tail somewhere to finish. `REVERB_DRAIN_SECONDS` comes from the FDN's feedback
  // coefficients, not from measuring a render, so it stays correct if the RT60 changes.
  //
  // Deliberately NOT folded into `loopTail`: lengthening the fold was measured audibly wrong
  // (a second musical passage over the opening), which is a separate problem from giving the
  // decay room.
  drain := max loopTail, REVERB_DRAIN_SECONDS
  offlineContext := new OfflineAudioContext(audioChannels, ceil(sampleRate * (endTime + dt + drain)), sampleRate)
  // offline analyser to sync with vfx frames
  // This limiter is the render's own instance, separate from the live one — retuning it
  // changes exports only, never interactive playback.
  //
  // The shared default sits at threshold 0dB, which aims the limiter AT full scale and
  // leaves its 5ms attack nothing to work with: transients overshoot and hard-clip at the
  // destination. That is fine with the app's quiet built-in soundfont and falls apart with
  // a loud one (GeneralUser GS runs ~12dB hotter; a dense arrangement pinned 10% of its
  // samples at 0dBFS). Attenuating into it does NOT help — at ratio 20:1, 12dB less input
  // bought only 2.6dB less output and still clipped. Headroom has to come from the
  // threshold itself.
  offlineLimiter := limiter offlineContext.destination
  offlineLimiter.threshold.value = limiterThreshold
  analyserDestination := stereoAnalyser offlineLimiter
  offlineFxNetwork := FXNetwork analyserDestination
  // Every fx preset feeds the network's master gain, so this attenuates all paths.
  if gain !== 1
    offlineFxNetwork.default.gain.value = gain

  channelData := new ChannelData song.ticksPerBeat, synth

  // Bind synth to offline context
  midiEventHandler := channelData.handleEvent

  //@ts-expect-error
  fxDest := offlineFxNetwork.fx[song.afxPresetName()] or offlineFxNetwork.default
  synthOut := synth.bind(channelData.channels, offlineContext)
  // The song's effect graph, if it has one, sits between the synth and the export's FX.
  spec := song.effects?()
  graph := if spec
    buildEffectGraph offlineContext, spec, song, synthOut, (synth as any).taps?(offlineContext),
      contextStartTime: 0
      playStartTime: 0
      rate: 1
  if graph
    graph.output.connect fxDest
  else
    synthOut.connect fxDest
  channelData.sync 0

  eventIterator := song.eventIterator()
  let frameId: number?

  // Every worklet the synth needs, not just the dB gain one — the reverb bus is built inside
  // `bind` once its module has registered, and rendering must not start before that.
  synth.prepareContext offlineContext
  // …and the graph's impulses and worklets, which must be in place before rendering starts.
  .then -> graph?.ready
  .then ->
    // TODO: Firefox doesn't support suspend on offline contexts so this will
    // be a big challenge to get it to render vfx synced to analyser data
    new Promise<AudioBuffer> (resolve, reject) ->
      // Step index rather than an accumulated t: adding dt a thousand times drifts by
      // enough to drop (or duplicate) the last frame, which is exactly the frame the
      // loop seam is made of.
      step .= -1
      frames .= 0

      progressView.message "Rendering audio..."
      progressView.value 0

      let work: () => void
      if offlineContext.suspend
        work = ->
          t := ++step * dt

          // Consume until following dt because `suspend` may occur after the passed
          // in `t` value by up to ~2ms
          // TODO: maybe make this 0.01 ?
          eventIterator.consumeUntilTime(t + dt, midiEventHandler)

          // Bounded by endTime — the context is only rendered that far, and there is
          // nothing past it that survives the slice below.
          if t <= endTime
            progressView.value t / endTime
            offlineContext.suspend(t)
            .then ->
              if startTime <= t <= endTime and frames < frameLimit
                frames++
                frameCallback!(t, analyserDestination)
            .then ->
              work()
              offlineContext.resume()
            .catch reject

          return
      else // Firefox and older Safari
        progressStep := ->
          progressView.value offlineContext.currentTime / endTime
          frameId = requestAnimationFrame progressStep

        work = ->
          progressStep()
          eventIterator.consumeUntilTime(endTime, midiEventHandler)

      work()

      p := offlineContext.startRendering()
      if p
        p.then(resolve, reject)
      else
        // Older Safari doesn't support the Promise version yet
        offlineContext.oncomplete = ({renderedBuffer}) ->
          resolve renderedBuffer
        // There is no `onerror` event

      return
  .then (renderedBuffer) ->
    limiterLatency(sampleRate).then (latency) ->
      // The limiter's lookahead put the music `latency` late in the buffer — see
      // limiterLatency. The drain renders well past that, so there is room to read it.
      at := startTime + latency
      if loopTail > 0
        foldLoopTail(renderedBuffer, at, duration!, loopTail)
      else
        fadeOutEnd(renderedBuffer, at, duration!)
      audioBufferToChannelData(renderedBuffer, at, duration!)
  .finally ->
    if frameId?
      cancelAnimationFrame frameId

/**
Fade the last `seconds` of the clip's audible content down to zero, in place.

The slice below cuts at a musical boundary, so the last sample is whatever level the music
was at — a step to digital silence, which is a click. That was survivable while every voice
was dry and short; with a reverb tail behind it there is always something ringing at the cut.

Only for clips that are NOT looping: a looping clip's end has to stay continuous with the
folded tail sitting on its head, and a fade there would put the step back.

25ms is long enough to remove the step and short enough that it reads as the end of the
note rather than as a fade out. Raised cosine, so the envelope has no corner of its own.
*/
fadeOutEnd := (buffer: AudioBuffer, startTime: number, duration: number, seconds = 0.025) ->
  {sampleRate} := buffer

  e := ceil (startTime + duration) * sampleRate
  s := floor startTime * sampleRate
  n := min floor(seconds * sampleRate), e - s

  return unless n > 0

  for i of [0...buffer.numberOfChannels]
    samples := buffer.getChannelData i

    for j of [0...n]
      // 1 at the start of the fade, 0 at the final sample.
      samples[e - n + j] *= (1 + cos(PI * j / n)) / 2

  return

/**
Add the decay rendered past the clip's end onto its beginning, in place, so the clip
joins to itself when it repeats.

The clip is cut at a musical boundary, not a silent one: notes are still sounding and
the reverb is still ringing on the last sample, and playback restarts from whatever the
first sample happens to be. That step is the click. Everything that was still decaying
past the end is exactly what a listener expects to hear over the restart, so add it there.

The tail is SHORT and fades from its first sample. Its job is to bridge a one-sample step,
which takes tens of milliseconds, not to replay the ending over the opening. A first
attempt folded 1.5s at full level for the first half of it, and on a song that ends
mid-phrase that is a second passage playing over the top of the first — measured adding
rms 0.371 against the song's own opening at 0.362, audible as a clump of notes at the
start. At 150ms with a cosine decay the bridge is there and there is nothing to pick out
as a second part.

The clip's own first samples get a 5ms fade in first. A clip cut out of the middle of a
song starts at full level in an arbitrary phase, and that step survives the fold — the
folded tail joins the end smoothly, but the music underneath it still switches on. 5ms is
below the threshold of hearing an attack soften and is plenty to remove the step. On a
whole song, which starts from silence, it changes nothing.
*/
foldLoopTail := (buffer: AudioBuffer, startTime: number, duration: number, tail: number) ->
  {sampleRate} := buffer

  s := floor startTime * sampleRate
  e := ceil (startTime + duration) * sampleRate
  n := min floor(tail * sampleRate), buffer.length - e

  return unless n > 0

  // Raised cosine both ways: no corner in the envelope, so nothing to hear as a chirp.
  fadeIn := min floor(0.005 * sampleRate), n

  for i of [0...buffer.numberOfChannels]
    samples := buffer.getChannelData i

    for j of [0...fadeIn]
      samples[s + j] *= (1 - Math.cos(Math.PI * j / fadeIn)) / 2

    // Gain 1 at j=0 — the seam sample itself has to be continuous, that is the whole
    // point — decaying to 0 across the tail.
    for j of [0...n]
      gain := (1 + Math.cos(Math.PI * j / n)) / 2
      samples[s + j] += samples[e + j] * gain

  return

type ExportSongOptions = Partial<{name: string, sampleRate: number, type: "mp3" | "wav"}>
